import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink } from '../components/Nav';
import { CheckIcon } from '../components/icons';
import { Button, ErrorText, Spinner } from '../components/ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { paywallPath } from '../lib/plans';
import { decideOAuthRequest, fetchOAuthRequest } from '../queries/oauth';

/**
 * Letting an MCP client sign in as you.
 *
 * Claude (or Cursor, or anything else that speaks MCP) was given the address
 * of Slate's MCP server and has come to ask for a session. The API recorded
 * what it asked for and sent the browser here. Like the device page, the job
 * is to show the human exactly what they are about to let in, and to make
 * saying no as easy as saying yes. Either answer sends the browser back to
 * the client, which is where the person came from.
 */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>
        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function AuthorizePage({ requestId }: { requestId: string }) {
  const { user, loading } = useAuth();
  const [decided, setDecided] = useState<'approve' | 'deny' | null>(null);

  const details = useQuery({
    // Keyed on who is looking: the answer says whether their plan includes
    // this, and signing in changes it.
    queryKey: ['oauth-request', requestId, user?.uid ?? null],
    queryFn: () => fetchOAuthRequest(requestId),
    enabled: !loading,
    retry: false,
  });

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'deny') => decideOAuthRequest(requestId, decision),
    onSuccess: (data, decision) => {
      track(decision === 'approve' ? 'MCP Client Authorized' : 'MCP Client Denied', { client: details.data?.client.name });
      setDecided(decision);
      // Back to the client with the answer. A moment's pause so the page
      // can say what happened before it goes.
      window.setTimeout(() => window.location.assign(data.redirectTo), 600);
    },
  });

  const clientName = details.data?.client.name ?? 'An app';

  if (loading || (details.isPending && !details.isError)) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2.5 py-6 text-chalk-faint">
          <Spinner />
          <span className="text-body">Checking your session…</span>
        </div>
      </Shell>
    );
  }

  if (details.isError) {
    return (
      <Shell>
        <h1 className="text-h3 text-chalk">This request is not valid</h1>
        <p className="mt-2 text-body text-chalk-dim">
          {details.error instanceof ApiError && details.error.isNotFound
            ? 'It may have expired: the app has ten minutes to be let in. Go back to the app and try connecting again.'
            : errorMessage(details.error)}
        </p>
      </Shell>
    );
  }

  if (!user) {
    const here = `${window.location.pathname}${window.location.search}`;
    return (
      <Shell>
        <h1 className="text-h3 text-chalk">Sign in to connect {clientName}</h1>
        <p className="mt-2 text-body text-chalk-dim">
          {clientName} is asking to sign in as you. Sign in here first and you will be brought back to
          this page. The request keeps working for a few minutes.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <AppLink to={`/login?next=${encodeURIComponent(here)}`} className="sm:flex-1">
            <Button variant="primary" size="lg" className="w-full">
              Sign in
            </Button>
          </AppLink>
          <AppLink to={`/signup?next=${encodeURIComponent(here)}`} className="sm:flex-1">
            <Button size="lg" className="w-full">
              Create an account
            </Button>
          </AppLink>
        </div>
      </Shell>
    );
  }

  if (decided === 'approve') {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-go">
          <CheckIcon size={18} />
          <span className="text-caption uppercase tracking-[0.08em]">Connected</span>
        </div>
        <h1 className="mt-2 text-h3 text-chalk">{clientName} is signed in</h1>
        <p className="mt-2 text-body text-chalk-dim">Sending you back to {clientName}…</p>
        <p className="mt-4 text-caption text-chalk-faint">
          You can disconnect it at any time from Settings, and it stops working immediately.
        </p>
      </Shell>
    );
  }

  if (decided === 'deny') {
    return (
      <Shell>
        <h1 className="text-h3 text-chalk">Nothing was connected</h1>
        <p className="mt-2 text-body text-chalk-dim">
          The request was turned down. If that was not you, no action is needed: nothing was granted.
        </p>
      </Shell>
    );
  }

  // A Team feature. The recording tools would refuse a free seat anyway;
  // better to say so here, with the way in, than to connect an app that
  // cannot do anything.
  if (details.data && details.data.included === false) {
    return (
      <Shell>
        <span className="text-caption uppercase tracking-[0.08em] text-chalk-faint">Connect an app</span>
        <h1 className="mt-2 text-h3 text-chalk">{clientName} wants to read your recordings</h1>
        <p className="mt-2 text-body text-chalk-dim">
          Connecting Claude and other AI tools to Slate comes with Team: they can find a recording, read the
          transcript and what was on screen, take screenshots at any moment, and tag or comment on it, all as you.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <AppLink to={paywallPath('mcp')} className="sm:flex-1" onClick={() => track('Paywall Opened', { feature: 'mcp' })}>
            <Button variant="primary" size="lg" className="w-full">
              See the Team plan
            </Button>
          </AppLink>
          <Button
            size="lg"
            className="sm:flex-1"
            busy={decide.isPending}
            disabled={details.data.alreadyHandled}
            onClick={() => decide.mutate('deny')}
          >
            Not now
          </Button>
        </div>
        <p className="mt-4 text-caption text-chalk-faint">
          Once your seat is on Team, ask {clientName} to connect again; the request itself lasts ten minutes.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <span className="text-caption uppercase tracking-[0.08em] text-chalk-faint">Connect an app</span>
      <h1 className="mt-2 text-h3 text-chalk">Let {clientName} use Slate as you?</h1>
      <p className="mt-2 text-body text-chalk-dim">
        It would sign in as <span className="text-chalk">{user.email}</span>
        {details.data?.redirectHost ? (
          <>
            {' '}
            and go back to <span className="text-chalk">{details.data.redirectHost}</span>
          </>
        ) : null}
        .
      </p>

      <div className="mt-4 rounded-lg bg-slate-900 p-4">
        <p className="text-caption text-chalk-faint">It will be able to</p>
        <ul className="mt-2 space-y-1 text-body text-chalk">
          <li>Find and open recordings you can see, with their transcripts, comments and screenshots</li>
          <li>Add tags and comments, and rename recordings you own</li>
        </ul>
        <p className="mt-3 text-caption text-chalk-dim">
          It cannot delete recordings, change who can watch them, or touch billing or your team.
        </p>
      </div>

      {details.data?.alreadyHandled ? (
        <p className="mt-3 text-caption text-caution">
          This request has already been answered. Ask the app to connect again.
        </p>
      ) : null}

      {decide.isError ? (
        <div className="mt-3">
          <ErrorText>{errorMessage(decide.error)}</ErrorText>
        </div>
      ) : null}

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Button
          variant="primary"
          size="lg"
          className="sm:flex-1"
          busy={decide.isPending && decide.variables === 'approve'}
          disabled={!details.data || details.data.alreadyHandled}
          onClick={() => decide.mutate('approve')}
        >
          Connect {clientName}
        </Button>
        <Button
          size="lg"
          className="sm:flex-1"
          busy={decide.isPending && decide.variables === 'deny'}
          disabled={!details.data || details.data.alreadyHandled}
          onClick={() => decide.mutate('deny')}
        >
          Not me
        </Button>
      </div>

      <p className="mt-4 text-caption text-chalk-faint">
        If you did not just ask an app to connect to Slate, choose Not me.
      </p>
    </Shell>
  );
}
